import { c as _c } from "react/compiler-runtime";
import { useRef } from 'react';
import { useFetch } from 'shared/hooks/use-fetch';
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types';
import useUser from 'shared/hooks/use-user';
import { LimitType, SwrPaginationInterface } from 'shared/types/pagination-interface';
import { getPaginationAndFilterQueryString } from 'shared/utils/api/get-pagination-and-filter-query-string';
import { NOTIFICATIONS_API_URL } from '../api/notifications-api';
import { NotificationsApiInterface, NotificationsFilterInterface } from '../types/notification-types';
import { useControlledSwr } from 'shared/hooks/use-controlled-swr';
import { RequestModuleEnum } from 'shared/enums/request-module-enum';
import { useAwaitedSharingNotification } from 'modules/share/hooks/use-awaited-sharing-notification';
import { useAwaitedStudentProgressExportNotification } from 'modules/courses/students/hooks/use-awaited-student-progress-export-notification';
type UseNotificationsInterface = Omit<SwrPaginationInterface, 'limit'> & {
  shouldFetchNotifications?: boolean;
  filter: NotificationsFilterInterface;
  limit: LimitType | number;
};
export interface HasUnreadResponse {
  hasUnread: boolean;
}
const NOTIFICATIONS_REFRESH_INTERVAL = 1500;
const NOTIFICATIONS_MAX_REFRESH_RETRIES = 10;
export const useNotifications = t0 => {
  const $ = _c(19);
  const {
    limit,
    shouldFetchNotifications: t1,
    pagination,
    filter
  } = t0;
  const shouldFetchNotifications = t1 === undefined ? true : t1;
  const refreshRetriesRef = useRef(0);
  const {
    user
  } = useUser();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      method: RequestMethodsEnum.get
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    fetcher,
    isFetching
  } = useFetch(t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      method: RequestMethodsEnum.get
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    fetcher: unreadFetcher
  } = useFetch(t3);
  const {
    isThereAwaitedSharing
  } = useAwaitedSharingNotification();
  const {
    isThereAwaitedStudentProgressExport
  } = useAwaitedStudentProgressExportNotification();
  let t4;
  if ($[2] !== filter || $[3] !== limit || $[4] !== pagination || $[5] !== shouldFetchNotifications || $[6] !== user) {
    t4 = () => {
      if (user && shouldFetchNotifications) {
        return [`${NOTIFICATIONS_API_URL}/notifications?${getPaginationAndFilterQueryString({
          limit: limit as LimitType,
          pagination,
          filter: {
            ...filter
          }
        })}`, RequestModuleEnum.Notifications];
      }
      return null;
    };
    $[2] = filter;
    $[3] = limit;
    $[4] = pagination;
    $[5] = shouldFetchNotifications;
    $[6] = user;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== isThereAwaitedSharing || $[9] !== isThereAwaitedStudentProgressExport) {
    t5 = {
      refreshInterval(latestData) {
        const shouldRefresh = isThereAwaitedSharing(latestData) || isThereAwaitedStudentProgressExport(latestData);
        if (!shouldRefresh) {
          refreshRetriesRef.current = 0;
          return 0;
        }
        if (refreshRetriesRef.current >= NOTIFICATIONS_MAX_REFRESH_RETRIES) {
          return 0;
        }
        refreshRetriesRef.current = refreshRetriesRef.current + 1;
        return NOTIFICATIONS_REFRESH_INTERVAL;
      },
      errorRetryCount: 3
    };
    $[8] = isThereAwaitedSharing;
    $[9] = isThereAwaitedStudentProgressExport;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    data: notifications,
    mutate
  } = useControlledSwr(t4, fetcher, t5);
  let t6;
  if ($[11] !== user) {
    t6 = () => {
      if (user) {
        return [`${NOTIFICATIONS_API_URL}/notifications/has-unread`, RequestModuleEnum.Notifications];
      }
      return null;
    };
    $[11] = user;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const {
    data: notificationsMeta,
    mutate: mutateNotificationsMeta
  } = useControlledSwr(t6, unreadFetcher);
  let t7;
  if ($[13] !== isFetching || $[14] !== mutate || $[15] !== mutateNotificationsMeta || $[16] !== notifications || $[17] !== notificationsMeta) {
    t7 = {
      notificationsMeta,
      mutateNotificationsMeta,
      notifications,
      isFetching,
      mutate
    };
    $[13] = isFetching;
    $[14] = mutate;
    $[15] = mutateNotificationsMeta;
    $[16] = notifications;
    $[17] = notificationsMeta;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
};